<template lang='pug'>
  #product-reviews
    ProductDetails
    .rating-filter-container#review-filter-section(v-if='isEnhancedMwebReviewPage')
      .product-rating-summary
        ProductRatingSummary(:reviewMeta='reviewMeta' targetContainer='review-page' v-if='displayRatingsSummaryCard')
      ProductReviewsFilterSort
    template(v-else)
      ProductRatingSummary(:reviewMeta='reviewMeta' targetContainer='review-page' v-if='displayRatingsSummaryCard')
      ProductReviewsFilterSort

    template(v-if='!noFilterReview && !this.reviewsLoading')
      .pagination-recap {{ paginationRecap }}
    ProductReviewList

    template(v-if='reviewsCount > 10')
      Pagination(:full='true' :startPage='reviewsPage' :pageCount='reviewPageCount' @setPage='setPage')
</template>

<script>
import { mapState } from 'vuex'
import { pushDataLayerEvent } from '@/modules/services/gtm'
import { reviewsMixin } from '@/mixins/reviews-mixin'
import ProductDetails from './product-details.vue'
import ProductRating from '../product-rating.vue'
import ProductReviewsFilterSort from './product-reviews-filter-sort.vue'
import ProductRatingSummary from '../product-rating-summary.vue'
import ProductReviewList from './product-review-list.vue'
import Pagination from '../../shared/pagination.vue'
import { dyUtil } from '@/mixins/dyUtil'
import { dynamicLoadStore } from '@/mixins/store-util'
import review from '@/store/modules/review'

export default {
  name: 'ProductsReviews',

  components: {
    ProductDetails,
    ProductRating,
    ProductReviewsFilterSort,
    ProductRatingSummary,
    ProductReviewList,
    Pagination
  },

  mixins: [
    dyUtil,
    reviewsMixin,
    dynamicLoadStore({ review })
  ],

  data () {
    return {
      isSummaryCardFeatureFlagOn: false,
      isEnhancedMwebReviewPage: false,
      isFilterSectionSticky: false
    }
  },

  computed: {
    ...mapState({
      product: state => state.product.product,
      variants: state => state.product.productVariants,
      reviews: state => state.review.reviews,
      reviewsPage: state => state.review.page,
      reviewsCount: state => state.review.count,
      reviewMeta: state => state.review.meta,
      reviewsLoading: state => state.review.reviewsLoading
    }),
    reviewPageCount () {
      return Math.ceil(this.reviewsCount / 10)
    },
    noFilterReview () {
      return this.reviewMeta.total === 0 && this.isSummaryCardFeatureFlagOn
    },
    paginationRecap () {
      if (!this.reviewMeta) return

      const from = (this.reviewMeta['current-page'] - 1) * this.reviewMeta['per-page'] + 1
      const to = this.reviewMeta['current-page'] * this.reviewMeta['per-page']
      const total = this.reviewMeta['total-items']
      const subject = this.$t('account.reviews.title')
      return this.$t('meta.pagination.viewingRecap', {
        from: from,
        to: to < total ? to : total,
        total: total,
        subject: subject
      })
    },
    displayRatingsSummaryCard () {
      return this.isSummaryCardFeatureFlagOn && this.reviewMeta['total-ratings'] !== 0
    }
  },

  async created () {
    // Check if the store contains the intended product attributes
    if (Object.keys(this.$store.state.product.product).length === 0 ||
        (this.$store.state.product.product.id !== this.$route.params.id &&
          this.$store.state.product.product.attributes['slug-url'] !== this.$route.params.id)) {
      try {
        await this.$store.dispatch('fetchProduct', {
          id: this.$route.params.id
        })

        this.updateSEO()

        if (this.product.attributes) {
          this.$store.commit('setCurrentVariant', this.variants[0])
        } else {
          this.$router.replace('/')
          luxola.updateFlash(this.$t('products.notAvailable', { product: this.$route.params.id }))
        }
      } catch {}
    }

    try {
      await this.fetchReviews()

      this.$nextTick(() => {
        pushDataLayerEvent('DynamicWindowLoad')
      })
    } catch {}

    this.initDY('REVIEW')
  },

  mounted () {
    this.isSummaryCardFeatureFlagOn = this.$featureFlag.getTreatment('enable_product_summary_card')
    this.isEnhancedMwebReviewPage = this.$kameleoon.isFeatureFlagActive('enable_enhanced_mweb_review_page')
    if (this.isEnhancedMwebReviewPage) {
      window.addEventListener('scroll', this.handleScroll)
    }
  },

  async destroyed () {
    this.$store.commit('resetReviewsFilters')
    await this.fetchReviews()
  },

  beforeDestroy () {
    if (this.isEnhancedMwebReviewPage) {
      window.removeEventListener('scroll', this.handleScroll)
    }
  },

  methods: {
    setPage (page, isBottom) {
      this.$store.commit('setReviewsPage', page)
      this.recordPaginationGtmEvent(page)
      this.fetchReviews()
      this.$scrollTo('#product-reviews', 400, {
        offset: -80
      })
    },

    updateSEO () {
      const brand = this.variants[0].attributes['brand-name']
      const name = this.product.attributes.name
      const pageTitle = `${brand} ${name} Reviews`
      const categories = this.$store.state.category.categories.map(c => c.attributes['label']).join(',')
      const metaDescription = this.product.attributes.seo['meta-description']
      let description = this.product.attributes.description
      description = description.length > 100 ? `${description.substring(0, 100)}...` : description

      this.$store.commit('setSeoMetaTitle', pageTitle)
      this.$store.commit('setSeoMetaDescription', metaDescription || this.$t('products.seo.description', { brand, name, description }))
      this.$store.commit('setSeoMetaKeywords', this.$t('products.seo.keywords', { brand, name, categories }))
      this.$store.commit('setSeoOgUrl', this.product.attributes['web-url'])
      this.$store.commit('setSeoUpdate', true)
    },

    handleScroll () {
      const appBanner = document.getElementsByClassName('get-app-banner')

      if (appBanner.length > 0) {
        const closeButton = appBanner[0].getElementsByClassName('close')[0]
        closeButton.addEventListener('click', this.handleCloseBtnClick)
      }

      const productDetailsHeight = this.$el.querySelector('.product-details').clientHeight
      if (window.scrollY >= productDetailsHeight) {
        if (!this.isFilterSectionSticky) {
          this.stickFilterSection()
        }
      } else {
        if (this.isFilterSectionSticky) {
          this.unstickFilterSection()
        }
      }
    },

    handleCloseBtnClick () {
      const reviewFiltersSection = document.getElementById('review-filter-section')
      if (this.isFilterSectionSticky) {
        reviewFiltersSection.style.top = `${ this.headerSectionHeight() }px`
      }
    },

    headerSectionHeight () {
      const appBannerHeight = $('.get-app-banner').outerHeight() || 0
      const headerHeight = $('#header').outerHeight() || 0
      return headerHeight + appBannerHeight
    },

    stickFilterSection () {
      const reviewFiltersSection = document.getElementById('review-filter-section')
      this.isFilterSectionSticky = true
      reviewFiltersSection.classList.add('sticky')
      reviewFiltersSection.classList.remove('static')
      reviewFiltersSection.style.top = `${ this.headerSectionHeight() }px`
    },

    unstickFilterSection () {
      const reviewFiltersSection = document.getElementById('review-filter-section')
      this.isFilterSectionSticky = false
      reviewFiltersSection.classList.remove('sticky')
      reviewFiltersSection.classList.add('static')
    }
  }
}
</script>

<style scoped lang='scss'>
  #product-reviews {
    margin-bottom: 20px;
  }

  .sticky {
    position: fixed;
    width: 95%;
    z-index: 1;
  }

  .static {
    position: static;
    width: 100%;
  }

  .pagination-recap {
    height: 14px;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0.32px;
    line-height: 17px;
    margin-top: 2em;
  }

  .rating-filter-container {
    transition: all .8s cubic-bezier(.455,.03,.515,.955);
    transition-timing-function: cubic-bezier(.455,.03,.515,.955);
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 6px 4px;
    background: white;
    border-bottom: 1px solid #ccc;
    gap: 15px;

    .product-rating-summary {
      padding: 0px;
    }
  }
</style>
